import { useEffect, useState } from "react";
import { useApi } from "../../api";
import { PageHeader } from "../GenericPage";
import { n0 } from "../../lib/format";
import DataTable from "../../lib/DataTable";
import { Notice } from "../../lib/ui";

/*
 * Inventory overview – port of stockAnalysis.php (API: GET /api/stock/overview)
 * KPI cards (click to open the product lists), stock quantity by product type, and drill-down tables.
 */

const COLS = [
  { key: "product_name", label: "Product", render: (r) => <b style={{ color: "var(--ink)" }}>{r.product_name}</b> },
  { key: "manufacturer", label: "Manufacturer" }, { key: "product_type", label: "Type" },
  { key: "quantity", label: "Quantity", num: true, render: (r) => n0(r.quantity) },
  { key: "minimum_stock", label: "Min stock", num: true, render: (r) => n0(r.minimum_stock) },
];

export default function InventoryOverview() {
  const api = useApi();
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null); // "all" | "low"
  useEffect(() => { api.get("/api/stock/overview").then(setD).catch((e) => setError(e.message)); }, [api]);
  const max = Math.max(1, ...(d?.by_type || []).map((t) => t.quantity));

  const Card = ({ id, icon, tone, value, label }) => {
    const inner = <><span className={`ui-stat-ic ${tone}`}><i className={`fas ${icon}`} /></span>
      <div><div className="ui-stat-label">{label}</div><div className="ui-stat-value mono">{value}</div></div></>;
    return id ? <button type="button" className={`ui-stat io-card ${open === id ? "on" : ""}`} aria-pressed={open === id} onClick={() => setOpen(open === id ? null : id)}>{inner}</button>
      : <div className="ui-stat">{inner}</div>;
  };

  return (
    <main className="pg">
      <PageHeader slug="stockAnalysis" />
      <Notice notice={error && { tone: "err", text: error }} />
      {d && (
        <>
          <div className="ui-stats">
            <Card id="all" icon="fa-box" tone="brand" value={n0(d.total_products)} label="Total products · click to list" />
            <Card icon="fa-cubes" tone="muted" value={n0(d.total_stock)} label="Total stock quantity" />
            <Card id="low" icon="fa-triangle-exclamation" tone="danger" value={n0(d.low_stock.length)} label="Below minimum · click to list" />
          </div>
          <section className="panel" style={{ padding: "1.1rem 1.25rem", marginBottom: "1.25rem" }}>
            <h3 style={{ fontSize: ".95rem", margin: "0 0 .9rem" }}>Stock by product type</h3>
            <div className="io-bars" role="img" aria-label="Total quantity by product type">
              {d.by_type.map((t) => (
                <div key={t.type} className="io-bar">
                  <span className="io-label" title={t.type}>{t.type}</span>
                  <div className="sr-track" style={{ height: ".8rem" }}><div style={{ width: `${(t.quantity / max) * 100}%` }} /></div>
                  <span className="mono io-val">{n0(t.quantity)}</span>
                </div>
              ))}
            </div>
          </section>
          {open && (
            <section className="panel">
              <div style={{ display: "flex", justifyContent: "space-between", padding: "1rem 1.25rem", borderBottom: "1px solid var(--line)" }}>
                <h3 style={{ fontSize: "1rem", margin: 0 }}>{open === "all" ? "All products" : "Products below minimum stock"}</h3>
                <button type="button" className="btn sm" onClick={() => setOpen(null)} aria-label="Close list"><i className="fas fa-xmark" /></button>
              </div>
              <DataTable columns={COLS} rows={open === "all" ? d.products : d.low_stock} rowKey={(r) => r.product_name}
                empty={open === "low" ? "All products are above minimum stock." : "No products."} />
            </section>
          )}
        </>
      )}
    </main>
  );
}
